import * as _ from "lodash";
import Config from "@skin_config";
import { Swarm } from "@lbs/swarm-client";
import Helpers from "@common/helpers/helperFunctions";
import Translator from "@common/helpers/translator";
import { persistStore } from "redux-persist";
import { setOddConverterPartnerConfig } from "@common/helpers/oddConverter";
import { swarmRequest } from "@common/helpers/util/requestMiddleware";
import { getAuthData } from "@common/helpers/auth";
import { getPreferencesContextValue } from "@common/contexts/preferences";
import { broadcast, broadcastChannels } from "@common/helpers/broadcast";
import { checkTerminalMode } from "@common/helpers";
import { setFontCSSVariables } from "@common/helpers/view";
import {
  SetLanguage,
  SetTerminalAuthData,
  SetBonuses,
  SetTax,
  getMerchantInfo,
  getSavedPlayerPaymentInfo,
  setLastMoneyInputType,
  setTerminalDepositMode,
} from "../actions/terminal";
import { AppReady } from "../actions";
import countries from "../constants/countries";
import { updateTranslations } from "../constants/errorCodes";
import { SwarmConfigData } from "../actions/initialData";
import setupTerminalFunctionalities from "../helpers/terminalSpecific";
import { setSwarmBetshopSessionInfo } from "../helpers/terminalRequests/cashdesk";
import PaymentsService from "../services/payments";
import { isMultiDepositModeEnabled } from "../helpers/multiDepositMode";
import { getNetworkTime } from "../../../BettingTerminal.Common/helpers/networkTime";
import { TerminalBalanceFetcher } from "../helpers/terminalBalance";
import { getTerminalLanguage } from "../helpers/languageByIdOrConfig";

Config.main.enableTouchToClick &&
  window.addEventListener("touchstart", (e) => {
    if (e.touches.length > 1) e.target.click();
  });

const setupTerminalParams = async (store) => {
  const pid = Helpers.getUriParam("pid");
  const tid = Helpers.getUriParam("tid");
  let token = Helpers.getUriParam("token");
  const currency = Helpers.getUriParam("currency");
  const version = Helpers.getUriParam("version");
  const dualscreen = Helpers.getUriParam("dualscreen");
  const skinType = Helpers.getUriParam("skin");
  const betshopid = Helpers.getUriParam("betshopid");
  const enableOnlinePayment = Helpers.getUriParam("enableOnlinePayment");
  token = token && token.replace(" ", "+"); // todo delete after Khikar makes release

  if (token || pid || tid || currency || skinType || betshopid || enableOnlinePayment) {
    sessionStorage.setItem(
      "authData",
      JSON.stringify({
        pid,
        tid,
        token,
        currency,
        skinType,
        betshopid,
        enableOnlinePayment,
      })
    );
  } else {
    const authData = JSON.parse(sessionStorage.getItem("authData"));
    if (!authData || !Object.keys(authData).length) {
      sessionStorage.setItem("authData", JSON.stringify(Config.defaultTerminalParams));
    }
  }
  dualscreen !== undefined && sessionStorage.setItem("dualscreen", dualscreen);
  version !== undefined && sessionStorage.setItem("version", JSON.stringify({ version }));

  if (!_.isEmpty(Config.main.blockedProviders)) {
    const country = await fetch("https://ipapi.co/json").then((res) => res.json());
    localStorage.setItem("countryCode", country?.country_code);
  }

  const authData = JSON.parse(sessionStorage.getItem("authData"));
  store.dispatch(SetTerminalAuthData({ authData }));
};

const sendLanguageToApp = (store) => {
  const [preferences] = getPreferencesContextValue();
  const { lang } = preferences;
  setTimeout(() => store.dispatch(SetLanguage(Config.swarm.languageMap[lang] || lang)), 0);
};
const initTerminalDepositMode = async (store) => {
  const { state: sessionBalanceState, playerInfo } = await PaymentsService.getSessionBalanceState();

  if (sessionBalanceState) {
    store.dispatch(setLastMoneyInputType(sessionBalanceState));
    store.dispatch(setTerminalDepositMode(sessionBalanceState));
  }

  if (playerInfo?.PersonalId) await PaymentsService.setPlayerInfo(playerInfo);
};
const Bootstrap = async (store) => {
  setFontCSSVariables(Config.main.fontName);

  await loadConfig(store);
  await setupTerminalParams(store);

  return loadTranslations(store).then(() => {
    updateTranslations();
    setupTerminalFunctionalities(store).then(async () => {
      sendLanguageToApp(store);
      store.dispatch(getMerchantInfo());
      store.dispatch(getSavedPlayerPaymentInfo());

      if (checkTerminalMode(Config, "betting")) {
        initSwarm(store);
        const enableOnlinePayment = Helpers.getUriParam("enableOnlinePayment");
        if (isMultiDepositModeEnabled()) await initTerminalDepositMode(store);
        await TerminalBalanceFetcher.fetchAndStore();

        if (Config.betslip.enableExpressBonus) {
          await swarmRequest({ include_exact_losing: true }, "get_sport_bonus_rules").then((response) =>
            store.dispatch(SetBonuses(response?.details))
          );
        }
      }

      store.dispatch(AppReady());
      window.appReady = store.getState().appReady;
      window.jsobject ? window.jsobject.appReady() : window.external.appReady();
    });
  });
};

export default Bootstrap;

const initSwarm = async (store) => {
  const [{ url: swarmUrl }] = Config.swarm.websocket;
  const [{ lang }] = getPreferencesContextValue();
  const terminalLang = getTerminalLanguage();
  const sessionLanguage = lang || terminalLang;
  const langToUse = Config.swarm.languageMap[sessionLanguage] || sessionLanguage;

  const swarmSessionParams = {
    language: langToUse,
    site_id: getAuthData()?.pid,
    terminal: getAuthData()?.tid || Config.defaultTerminalParams.tid,
  };

  const swarmInstance = Swarm.init(swarmUrl, swarmSessionParams, Config.swarm);

  swarmInstance.listeners.addEventListener("connected", (response) => {
    const data = { connected: true };
    broadcast.dispatchEvent(new CustomEvent(broadcastChannels.SocketStateChange, { detail: data }));

    const authData = getAuthData();

    if (authData) setSwarmBetshopSessionInfo();
  });

  swarmInstance.listeners.addEventListener("closed", () => {
    const data = { connected: false };
    broadcast.dispatchEvent(new CustomEvent(broadcastChannels.SocketStateChange, { detail: data }));
  });

  await swarmInstance.requestSession();

  const partnerConfig = swarmInstance.getPartnerConfig()

  setPartnerConfig(store, partnerConfig)
  swarmInstance.syncNetworkTime(getNetworkTime);
};

window.killSwarm = () => {
  Swarm.getInstance()._socket.close()
}

/**

Load translations
@returns {Promise}
*/
function loadTranslations(store) {
  return new Promise(async (resolve) => {
    const [preferences, setPreferences] = getPreferencesContextValue();
    const languageCodes = Object.keys(Config.main.availableLanguages);
    const prefix = Config.translations || `/languages/`;
    const countryCode = countries.iso2ToIso3[preferences.geoData && preferences.geoData.countryCode];
    const isLanguageAvailable = Config.main.availableLanguages.hasOwnProperty(countryCode);
    const terminalLang = getTerminalLanguage();
    const noTransKey = Helpers.getQueryStringValue("?notrans") === "id";
    const notrans = Helpers.getQueryStringValue("notrans") !== null;
    const lang = noTransKey
      ? "notrans"
      : preferences.lang ||
      (preferences.geoData && isLanguageAvailable && countryCode.toLowerCase()) ||
      terminalLang;
    Translator.setLanguage(lang);
    setPreferences({ ...preferences, lang });
    if (notrans && !noTransKey) resolve();
    else {
      try {
        console.log(`${prefix}${languageCodes[0]}.json?v=${Date.now()}`)
        await Promise.all(
          languageCodes.map((lang) =>
            fetch(`${prefix}${lang}.json?v=${Date.now()}`)
              .then((response) => response.json())
              .then((json) => {
                console.log(json, 'json', lang)
                if (json[lang]) {
                  Translator.setTranslations(json[lang], lang);
                }
              })
          )
        );

        resolve();
      } catch (ex) {
        console.warn("translations not loaded", ex);
        resolve(ex);
      }
    }
  });
}
const setPartnerConfig = (store, partnerConfig) => {
  setOddConverterPartnerConfig(partnerConfig.price_round_method);

  store.dispatch(SwarmConfigData(partnerConfig, "partner"));
  store.dispatch(
    SetTax({
      taxType: partnerConfig.tax_type,
      taxPercent: partnerConfig.tax_percent,
    })
  );
};
/**

Loads stored staloadConfige from local config and puts into store
Loads config froloadConfig external file
Merges external config with Config object
Merges saved preferences from store with ones defined in config
@param store
@returns {Promise}
*/
function loadConfig(store) {
  const notrans = Helpers.getQueryStringValue("?notrans") === "id";
  let allDone;
  const persistentStoreKeys = ["persistentUIState", "favorites"];
  const options = {
    whitelist: persistentStoreKeys,
  };
  const storeLoaded = new Promise((resolve) => persistStore(store, (() => options)(), resolve));
  let configLoaded; // load external config and merge with Config
  configLoaded = new Promise((resolve) => {
    if (Config.main.disableRemoteConfig) {
      resolve();
    } else {
      const configUrl = location.hostname === "localhost" ? "/conf.local.json" : "/conf.json";
      fetch(configUrl)
        .then((response) => response.json())
        .then((json) => {
          Helpers.mergeRecursive(Config, json);
          console.log("Merged config:", Config);
          resolve();
        })
        .catch((ex) => {
          console.warn("external config not loaded", ex, ", proceeding with default one");
          resolve(ex);
        });
    }
  });
  Promise.all([storeLoaded, configLoaded]).then(() => {
    const [preferences] = getPreferencesContextValue(); // Merge saved preferences from state with Config.env
    const terminalLang = getTerminalLanguage();
    let defaultLanguage = preferences.lang ? preferences.lang : terminalLang;
    const init = {
      ...preferences,
      lang: defaultLanguage,
    };
    defaultLanguage = Config.swarm.languageMap[defaultLanguage] || defaultLanguage;
    // store.dispatch(SetLanguage(defaultLanguage));
    // Config.betting.enableExpressBonus &&
    //  checkTerminalMode(Config, "betting") &&
    //  swarmRequest({}, "get_sport_bonus_rules").then(response =>
    //    store.dispatch(SetBonuses(response?.details))
    //  );
    if (preferences) {
      Helpers.mergeRecursive(Config.env, init);
    }
    allDone(notrans);
  });
  return new Promise((resolve) => {
    allDone = resolve;
  });
}
